import type { OrgNode } from '@portal/platform/session';

/**
 * The org-node tree as an indented list. Ported from portal-ui-lib's `orgnode-tree.util.ts`,
 * with one difference: a node whose parent is not in the list is a root. Angular dropped it,
 * which hides the top node of a user scoped below the tree's root.
 */

export interface FlatTreeItem {
  node: OrgNode;
  level: number;
  isExpanded: boolean;
  hasChildren: boolean;
}

const byName = (a: OrgNode, b: OrgNode) => a.name.localeCompare(b.name, 'da');

function index(nodes: readonly OrgNode[]) {
  const ids = new Set(nodes.map(node => node.id));
  const children = new Map<string, OrgNode[]>();
  const roots: OrgNode[] = [];
  for (const node of nodes) {
    if (node.parentId && ids.has(node.parentId)) {
      const siblings = children.get(node.parentId) ?? [];
      siblings.push(node);
      children.set(node.parentId, siblings);
    } else {
      roots.push(node);
    }
  }
  return { roots, children };
}

/** The visible rows: each root, then (if expanded) its children, depth first, by name. */
export function buildFlatTree(
  nodes: readonly OrgNode[],
  expandedIds: ReadonlySet<string>,
): FlatTreeItem[] {
  const { roots, children } = index(nodes);
  const rows: FlatTreeItem[] = [];
  const visit = (node: OrgNode, level: number) => {
    const kids = children.get(node.id) ?? [];
    const isExpanded = expandedIds.has(node.id);
    rows.push({ node, level, isExpanded, hasChildren: kids.length > 0 });
    if (isExpanded) {
      [...kids].sort(byName).forEach(child => visit(child, level + 1));
    }
  };
  [...roots].sort(byName).forEach(root => visit(root, 0));
  return rows;
}

/** Expanded at first: every root, and every ancestor of the current node so it is visible. */
export function initialExpandedIds(
  nodes: readonly OrgNode[],
  currentId: string | null | undefined,
): Set<string> {
  const { roots } = index(nodes);
  const expanded = new Set(roots.map(root => root.id));
  const byId = new Map(nodes.map(node => [node.id, node]));
  let current = currentId ? byId.get(currentId) : undefined;
  while (current?.parentId && byId.has(current.parentId)) {
    expanded.add(current.parentId);
    current = byId.get(current.parentId);
  }
  return expanded;
}
